import axios, { AxiosInstance, AxiosRequestConfig, AxiosPromise } from 'axios';
import { Observable } from 'rxjs';
import i18n from '../../plugins/i18n';
import { Log } from '@/utils';
import { ErrorResponse, failureKeys, FPError } from 'types';

export interface RxAxiosConfig {
  options: AxiosRequestConfig;
  interceptors?: {
    request: any;
    response?: {
      fulfilled: any;
      error: any;
    };
  };
}

export class RxAxios {
  private httpClient: AxiosInstance;

  constructor(configs: RxAxiosConfig) {
    this.httpClient = axios.create(configs.options);
    this.setInterceptors(configs);
  }

  private setInterceptors = (configs: RxAxiosConfig) => {
    if (!configs.interceptors) {
      return;
    }

    this.httpClient.interceptors.request.use(x =>
      configs.interceptors.request(x),
    );

    if (configs.interceptors.response) {
      this.httpClient.interceptors.response.use(
        response => configs.interceptors.response.fulfilled(response),
        error => configs.interceptors.response.error(error),
      );
    }
  };

  private makeRequest<T>(
    method: string,
    url: string,
    queryParams?: object,
    body?: object,
  ) {
    let request: AxiosPromise<T>;

    switch (method) {
      case 'GET':
        request = this.httpClient.get<T>(url, { params: queryParams });
        break;
      case 'POST':
        request = this.httpClient.post<T>(url, body, { params: queryParams });
        break;
      case 'PUT':
        request = this.httpClient.put<T>(url, body, { params: queryParams });
        break;
      case 'PATCH':
        request = this.httpClient.patch<T>(url, body, { params: queryParams });
        break;
      case 'DELETE':
        request = this.httpClient.delete(url, { params: queryParams });
        break;
      default:
        throw new Error('Method not supported');
    }
    return new Observable<T>(subscriber => {
      request
        .then(response => {
          if (
            'status' in (response.data as any) &&
            response.data['status'] === 'error'
          ) {
            throw response;
          }

          subscriber.next(response.data);

          subscriber.complete();
        })
        .catch(err => {
          Log.onBadRequst(err?.request?.responseURL, err?.status, err);

          const error = parseErrorResponse(err?.data);

          subscriber.error(error);
          subscriber.complete();
        });
    });
  }

  public get<T>(url: string, queryParams?: object) {
    return this.makeRequest<T>('GET', url, queryParams);
  }
  public post<T>(url: string, body: object, queryParams?: object) {
    return this.makeRequest<T>('POST', url, queryParams, body);
  }
  public put<T>(url: string, body: object, queryParams?: object) {
    return this.makeRequest<T>('PUT', url, queryParams, body);
  }
  public patch<T>(url: string, body: object, queryParams?: object) {
    return this.makeRequest<T>('PATCH', url, queryParams, body);
  }
  public delete(url: string, queryParams?: object) {
    return this.makeRequest('DELETE', url, queryParams);
  }
}

const parseErrorResponse = (data: any): FPError | null => {
  const unknownErrorMessage = i18n.global.t('errors.UNKNOWN');

  try {
    if (data === null) {
      return null;
    }

    const errorResponse: ErrorResponse = data;

    console.log(errorResponse);

    const mappedError: FPError = {
      header:
        errorResponse?.error ?? errorResponse?.data ?? failureKeys.UNKNOWN,
      message: errorResponse?.message || unknownErrorMessage,
    };

    return mappedError;
  } catch (error) {
    console.log(error);

    return {
      header: failureKeys.UNKNOWN,
      message: unknownErrorMessage,
    };
  }
};
